import { useCallback, useMemo, useState } from 'react';

import {
  add,
  endOfDay,
  isWithinInterval,
  startOfDay,
  eachHourOfInterval,
  isSameDay,
} from 'date-fns';

import useEventTypesStore from '@event/store/useEventTypesStore';

const useHoveredTimeSlots = () => {
  const [start, setStart] = useState<Date>();
  const { selectedEventTimeslotLength } = useEventTypesStore();
  const isFullDay = selectedEventTimeslotLength?.fullDay;
  const isTillMidnight = selectedEventTimeslotLength?.tillMidnight;
  const duration = selectedEventTimeslotLength?.duration || 1;

  const end = useMemo(() => {
    if (!start) {
      return undefined;
    }

    return isTillMidnight
      ? endOfDay(start)
      : add(start, { hours: duration - 1 });
  }, [start, isTillMidnight, duration]);

  const handleMouseEnter = useCallback((newStart: Date) => {
    setStart(newStart);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setStart(undefined);
  }, []);

  const isHovered = useCallback(
    (hour: Date) => {
      if (!start || !end) {
        return false;
      }

      const targetStart = isFullDay ? startOfDay(start) : start;

      return isWithinInterval(hour, { start: targetStart, end });
    },
    [start, end, isFullDay],
  );

  const middleOfHoveredTimeslot = useMemo(() => {
    if (!start || !end) {
      return undefined;
    }

    const targetStart = isFullDay ? startOfDay(start) : start;
    const targetEnd = isSameDay(end, start) ? end : endOfDay(start);

    const hours = eachHourOfInterval({ start: targetStart, end: targetEnd });

    return {
      startHour: targetStart,
      hour: hours[Math.floor(hours.length / 2)],
      isDividedByTwo: hours.length % 2 === 0,
    };
  }, [end, isFullDay, start]);

  return {
    handleMouseEnter,
    handleMouseLeave,
    isHovered,
    middleOfHoveredTimeslot,
    hoveredTimeslot: useMemo(
      () => (start && end ? { start, end } : undefined),
      [start, end],
    ),
  };
};

export default useHoveredTimeSlots;
